import type { GeoPoint } from './geo';

export interface Aircraft {
  hex: string;
  flight: string;
  lat: number;
  lon: number;
  altBaro: number | 'ground' | null;
  /** Ground speed in knots. */
  gs: number | null;
  /** Track (heading) in degrees. */
  track: number | null;
  /** ICAO aircraft type designator, e.g. "A388". */
  type: string | null;
  /** Registration, e.g. "D-AIMK". */
  reg: string | null;
  squawk: string | null;
  /** Seconds since last position report. */
  seen: number | null;
}

interface RawAc {
  hex?: string;
  flight?: string;
  lat?: number;
  lon?: number;
  alt_baro?: number | 'ground' | null;
  gs?: number;
  track?: number;
  t?: string;
  r?: string;
  squawk?: string;
  seen?: number;
}

export interface AircraftResult {
  aircraft: Aircraft[];
  total: number;
  source: string;
  fetchedAt: number;
}

const ADSB_BASES = ['https://api.adsb.lol', 'https://api.adsb.fi'];
const proxyUrl = (url: string) => `https://proxy.shakespeare.diy/?url=${encodeURIComponent(url)}`;

/**
 * Fetch live aircraft within a radius (nautical miles) of a point.
 * Tries several open ADS-B aggregators (adsb.lol, adsb.fi) plus a proxy fallback.
 */
export async function fetchAircraft(
  center: GeoPoint,
  radiusNm: number,
  signal?: AbortSignal,
): Promise<AircraftResult> {
  const r = Math.min(250, Math.max(25, Math.round(radiusNm)));
  const lat = center.lat.toFixed(3);
  const lon = center.lon.toFixed(3);
  const urls = [
    ...ADSB_BASES.map((base) => `${base}/v2/point/${lat}/${lon}/${r}`),
    proxyUrl(`${ADSB_BASES[0]}/v2/point/${lat}/${lon}/${r}`),
  ];

  let lastError: unknown = null;
  for (const url of urls) {
    try {
      const res = await fetch(url, { signal: signal ?? AbortSignal.timeout(12000) });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = (await res.json()) as { ac?: RawAc[]; total?: number };
      const aircraft: Aircraft[] = (data.ac ?? [])
        .filter(
          (a): a is RawAc & { lat: number; lon: number; hex: string } =>
            typeof a.lat === 'number' && typeof a.lon === 'number' && typeof a.hex === 'string',
        )
        .map((a) => ({
          hex: a.hex,
          flight: (a.flight ?? '').trim(),
          lat: a.lat,
          lon: a.lon,
          altBaro:
            typeof a.alt_baro === 'number' ? a.alt_baro : a.alt_baro === 'ground' ? 'ground' : null,
          gs: typeof a.gs === 'number' ? a.gs : null,
          track: typeof a.track === 'number' ? a.track : null,
          type: a.t ?? null,
          reg: a.r ?? null,
          squawk: a.squawk ?? null,
          seen: typeof a.seen === 'number' ? a.seen : null,
        }));
      return {
        aircraft,
        total: data.total ?? aircraft.length,
        source: new URL(url).hostname,
        fetchedAt: Date.now(),
      };
    } catch (err) {
      lastError = err;
    }
  }
  throw lastError instanceof Error ? lastError : new Error('All ADS-B sources unavailable');
}

/** Marker color by barometric altitude (feet). */
export function altitudeColor(altBaro: number | 'ground' | null): string {
  if (altBaro === 'ground') return '#94a3b8';
  if (altBaro == null) return '#a1a1aa';
  if (altBaro < 3000) return '#34d399';
  if (altBaro < 10000) return '#22d3ee';
  if (altBaro < 20000) return '#818cf8';
  if (altBaro < 32000) return '#c084fc';
  return '#f472b6';
}

export function formatAlt(altBaro: number | 'ground' | null): string {
  if (altBaro === 'ground') return 'GND';
  if (altBaro == null) return '—';
  return `${altBaro.toLocaleString()} ft`;
}
